import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import {
  useCerberus,
  RequiredAction,
  RequiredActionPriority,
} from "../../hooks/useCerberus";

interface ActionChoice {
  id: string;
  text: string;
  description?: string;
}

interface ChoiceActionMetadata {
  choices: ActionChoice[];
}

function asChoiceMetadata(value: unknown): ChoiceActionMetadata | null {
  if (
    typeof value === "object" &&
    value !== null &&
    "choices" in value &&
    Array.isArray((value as ChoiceActionMetadata).choices)
  ) {
    return value as ChoiceActionMetadata;
  }
  return null;
}

interface RequiredActionsDialogProps {
  open: boolean;
  onClose: () => void;
}

const RequiredActionsDialog: React.FC<RequiredActionsDialogProps> = ({
  open,
  onClose,
}) => {
  const { getCriticalActions, completeRequiredAction } = useCerberus();
  const [currentAction, setCurrentAction] = useState<RequiredAction | null>(
    null,
  );
  const [completing, setCompleting] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const criticalActions = getCriticalActions();
      if (criticalActions.length > 0) {
        setCurrentAction(criticalActions[0]);
      } else {
        setCurrentAction(null);
        onClose();
      }
    }
  }, [open, getCriticalActions, onClose]);

  const handleDialogClose = (
    _event: {},
    _reason: "backdropClick" | "escapeKeyDown",
  ) => {
    // Prevent closing critical actions by clicking outside or ESC
    if (currentAction?.priority === RequiredActionPriority.Critical) {
      return; // Block all attempts to close for critical actions
    }
    onClose();
  };

  const handleComplete = async (choiceId?: string) => {
    if (!currentAction) return;

    setCompleting(true);
    try {
      const completionData = choiceId
        ? { selectedChoice: choiceId }
        : undefined;

      const success = await completeRequiredAction(
        currentAction.id,
        currentAction.version,
        completionData,
      );

      if (success) {
        // Check if there are more critical actions
        const remainingActions = getCriticalActions();
        if (remainingActions.length > 1) {
          // Show next action
          setCurrentAction(remainingActions[1]);
        } else {
          // No more critical actions, close dialog
          onClose();
        }
      }
    } catch (error) {
      console.error("Failed to complete action:", error);
    } finally {
      setCompleting(false);
    }
  };

  const getPriorityText = (priority: RequiredActionPriority): string => {
    switch (priority) {
      case RequiredActionPriority.Critical:
        return "Critical";
      case RequiredActionPriority.High:
        return "High";
      case RequiredActionPriority.Medium:
        return "Medium";
      case RequiredActionPriority.Low:
        return "Low";
      default:
        return "Unknown";
    }
  };

  const getPriorityColor = (
    priority: RequiredActionPriority,
  ): "error" | "warning" | "info" | "default" => {
    switch (priority) {
      case RequiredActionPriority.Critical:
        return "error";
      case RequiredActionPriority.High:
        return "warning";
      case RequiredActionPriority.Medium:
        return "info";
      case RequiredActionPriority.Low:
        return "default";
      default:
        return "default";
    }
  };

  if (!currentAction) {
    return null;
  }

  // Check if this action has choices in metadata
  const choiceMetadata = asChoiceMetadata(currentAction.metadata);
  const hasChoices = choiceMetadata !== null;

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      data-testid="required-actions-dialog"
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
          {hasChoices ? currentAction.actionName : "Action Required"}
          <Chip
            label={getPriorityText(currentAction.priority)}
            color={getPriorityColor(currentAction.priority)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {!hasChoices && (
          <Typography variant="h6" gutterBottom>
            {currentAction.actionName}
          </Typography>
        )}

        {currentAction.description && (
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              marginBottom: "16px"
            }}>
            {currentAction.description}
          </Typography>
        )}

        {hasChoices && choiceMetadata && (
          <Box sx={{ mt: 2 }}>
            {choiceMetadata.choices.map((choice) => (
              <Card
                key={choice.id}
                sx={{
                  mb: 2,
                  cursor: "pointer",
                  border:
                    selectedChoice === choice.id ? "2px solid" : "1px solid",
                  borderColor:
                    selectedChoice === choice.id ? "primary.main" : "divider",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: 1,
                  },
                }}
                onClick={() => setSelectedChoice(choice.id)}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {choice.text}
                  </Typography>
                  {choice.description && (
                    <Typography variant="body2" sx={{
                      color: "text.secondary"
                    }}>
                      {choice.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {currentAction.version && (
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            Version: {currentAction.version}
          </Typography>
        )}

        {currentAction.expiresAt && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block"
            }}>
            Expires: {new Date(currentAction.expiresAt).toLocaleDateString()}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {currentAction.priority !== RequiredActionPriority.Critical && (
          <Button onClick={onClose} color="secondary">
            Dismiss
          </Button>
        )}
        {hasChoices ? (
          <Button
            onClick={() => handleComplete(selectedChoice || undefined)}
            color="primary"
            variant="contained"
            disabled={completing || !selectedChoice}
          >
            {completing ? "Completing..." : "Continue"}
          </Button>
        ) : (
          <Button
            onClick={() => handleComplete()}
            color="primary"
            variant="contained"
            disabled={completing}
          >
            {completing ? "Completing..." : "Complete"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RequiredActionsDialog;
